import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import OnboardingBanner from "@/components/OnboardingBanner";
import ChannexConnect from "@/components/ChannexConnect";
import api from "@/lib/api";
import { useIntegrationSetup, useAirbnbRateSetup } from "@/lib/queries";
import { useAuth } from "@/App";
import {
  CalendarBlank, Plus, Trash, ArrowsClockwise, CheckCircle,
  Envelope, DeviceMobile, Bell, Link, Info, Copy, Lock, LockOpen,
  WhatsappLogo, ArrowsLeftRight, FlowArrow, WarningCircle,
  ArrowElbowRight, Eye, EyeSlash
} from "@phosphor-icons/react";
import { toast } from "sonner";


const PLATFORM_META = {
  airbnb:  { label: "Airbnb",         color: "bg-red-50 text-red-600 border-red-200",       dot: "bg-red-400" },
  booking: { label: "Booking.com",    color: "bg-blue-50 text-blue-600 border-blue-200",    dot: "bg-blue-400" },
  vrbo:    { label: "VRBO",           color: "bg-indigo-50 text-indigo-600 border-indigo-200", dot: "bg-indigo-400" },
  mmt:     { label: "MakeMyTrip",     color: "bg-orange-50 text-orange-600 border-orange-200", dot: "bg-orange-400" },
  goibibo: { label: "Goibibo",        color: "bg-teal-50 text-teal-600 border-teal-200",    dot: "bg-teal-400" },
  agoda:   { label: "Agoda",          color: "bg-rose-50 text-rose-600 border-rose-200",    dot: "bg-rose-400" },
  other:   { label: "Other",          color: "bg-slate-50 text-slate-200 border-slate-200", dot: "bg-slate-400" },
};

function Toggle({ value, onChange, disabled }) {
  return (
    <button type="button" onClick={() => !disabled && onChange(!value)} disabled={disabled}
      className={`w-10 h-5.5 rounded-full transition-colors flex items-center px-0.5 ${value ? "bg-amber-400" : "bg-slate-200"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      style={{ height: "22px" }}>
      <motion.div animate={{ x: value ? 18 : 2 }} transition={{ type: "spring", stiffness: 500, damping: 30 }}
        className="w-4 h-4 rounded-full bg-white shadow-sm" />
    </button>
  );
}

function timeAgo(iso) {
  if (!iso) return null;
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)    return "Just now";
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function Integrations() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const setup = useIntegrationSetup(user?.user_id);
  const rateSetup = useAirbnbRateSetup(user?.user_id);
  const isAdmin   = user?.plan === "admin";
  const canUseChannelManager = ["starter", "pro", "agency", "admin"].includes(user?.plan);
  const [feeds, setFeeds]         = useState([]);
  const [properties, setProps]    = useState([]);
  const [automations, setAuto]    = useState(null);
  const [syncing, setSyncing]     = useState(false);
  const [adding, setAdding]       = useState(false);
  const [testingEmail, setTestEmail] = useState(false);
  const [locked, setLocked]       = useState(true);

  const [newFeed, setNewFeed] = useState({ url: "", platform: "airbnb", property_id: "", default_rate: "" });
  const [exportTokens, setExportTokens] = useState({});

  // Aiosell state
  const [aiosellConfigs, setAiosellConfigs] = useState({});
  const [aiosellTesting, setAiosellTesting] = useState({});
  const [aiosellSaving, setAiosellSaving] = useState({});
  const [aiosellSyncing, setAiosellSyncing] = useState({});
  const [roomConfigs, setRoomConfigs]       = useState({}); // room_id → {aiosell_room_code, aiosell_rate_plan_code}
  const [roomSaving, setRoomSaving]         = useState({}); // room_id → bool
  const [relayConfigs, setRelayConfigs]     = useState({}); // pid → {relay_webhook_url, relay_webhook_secret}
  const [relaySaving, setRelaySaving]       = useState({}); // pid → bool
  const [relaySecretVisible, setRelaySecretVisible] = useState({}); // pid → bool
  const [airbnbInputs, setAirbnbInputs]       = useState({}); // pid → {user_id, code}
  const [airbnbSubmitting, setAirbnbSubmitting] = useState({}); // pid → bool
  const [bulkModal, setBulkModal]             = useState(false);
  const [bulkParsed, setBulkParsed]           = useState([]); // [{name,url,platform,rate}]
  const [bulkImporting, setBulkImporting]     = useState(false);
  const [bulkResults, setBulkResults]         = useState(null);

  // Airbnb Rate Sync (bookmarklet) state
  const [rateSync, setRateSync]         = useState(null);   // { token, bookmarklet, ingest_url, properties }
  const [listingInputs, setListingInputs] = useState({});   // pid → airbnb_listing_id string
  const [mapSaving, setMapSaving]       = useState({});      // pid → bool

  const aiosellActivePids = new Set(
    properties
      .map(p => p.property_id || p.id)
      .filter(pid => {
        const prop = properties.find(pp => (pp.property_id || pp.id) === pid);
        const cfg  = aiosellConfigs[pid] || {};
        if (!cfg.aiosell_enabled || !cfg.aiosell_hotel_code) return false;
        if (prop?.unit_type === "hotel") {
          const rooms = prop.rooms || [];
          return rooms.length > 0 && rooms.every(r =>
            roomConfigs[r.room_id]?.aiosell_room_code && roomConfigs[r.room_id]?.aiosell_rate_plan_code
          );
        }
        return !!(cfg.aiosell_room_code && cfg.aiosell_rate_plan_code);
      })
  );
  const nonAiosellProps = properties.filter(p => !aiosellActivePids.has(p.property_id || p.id));

  useEffect(() => {
    if (!setup.data) return;
    const { feeds: f, properties: p, automations: a, configs: aio } = setup.data;
      setFeeds(Array.isArray(f) ? f : []);
      const props = Array.isArray(p) ? p : [];
      setProps(props);
      setAuto(a);
      if (Array.isArray(aio)) {
        const map = {};
        const relayMap = {};
        const airbnbMap = {};
        aio.forEach(c => {
          const key = c.property_id || c.id;
          if (key) {
            map[key] = c;
            relayMap[key] = {
              relay_webhook_url:    c.relay_webhook_url    || "",
              relay_webhook_secret: c.relay_webhook_secret || "",
            };
            airbnbMap[key] = {
              user_id: c.airbnb_user_id || "",
              code:    c.airbnb_code    || "",
            };
          }
        });
        setAiosellConfigs(map);
        setRelayConfigs(relayMap);
        setAirbnbInputs(airbnbMap);
      }
      // Initialise room configs from rooms embedded in hotel properties
      const rmMap = {};
      props.forEach(prop => {
        (prop.rooms || []).forEach(r => {
          rmMap[r.room_id] = {
            aiosell_room_code:      r.aiosell_room_code      || "",
            aiosell_rate_plan_code: r.aiosell_rate_plan_code || "",
          };
        });
      });
      setRoomConfigs(rmMap);
  }, [setup.data]);

  // Load the Airbnb rate-sync setup (token + bookmarklet + listing mappings)
  useEffect(() => {
    const data = rateSetup.data;
    if (!data) return;
        setRateSync(data);
        const map = {};
        (data.properties || []).forEach(p => { map[p.id] = p.airbnb_listing_id || ""; });
        setListingInputs(map);
  }, [rateSetup.data]);

  const saveListingMap = async (pid) => {
    setMapSaving(s => ({ ...s, [pid]: true }));
    try {
      await api.post("/airbnb-rates/map", {
        property_id: pid,
        airbnb_listing_id: listingInputs[pid] || "",
      });
      // Reflect the saved (digit-stripped) value locally
      setRateSync(rs => rs ? {
        ...rs,
        properties: (rs.properties || []).map(p =>
          p.id === pid ? { ...p, airbnb_listing_id: (listingInputs[pid] || "").replace(/\D/g, "") } : p),
      } : rs);
      toast.success(listingInputs[pid]?.trim() ? "Airbnb listing linked" : "Listing unlinked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the link");
    } finally {
      setMapSaving(s => ({ ...s, [pid]: false }));
    }
  };

  const updateAiosellField = (propertyId, field, value) => {
    setAiosellConfigs(prev => ({
      ...prev,
      [propertyId]: { ...(prev[propertyId] || { property_id: propertyId }), [field]: value },
    }));
  };

  const updateRoomField = (roomId, field, value) => {
    setRoomConfigs(prev => ({ ...prev, [roomId]: { ...(prev[roomId] || {}), [field]: value } }));
  };

  const saveRoomConfig = async (roomId) => {
    setRoomSaving(s => ({ ...s, [roomId]: true }));
    const cfg = roomConfigs[roomId] || {};
    try {
      await api.put(`/rooms/${roomId}`, {
        aiosell_room_code:      cfg.aiosell_room_code      || "",
        aiosell_rate_plan_code: cfg.aiosell_rate_plan_code || "",
      });
      toast.success("Room codes saved");
    } catch (err) {
      toast.error(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRoomSaving(s => ({ ...s, [roomId]: false }));
    }
  };

  const saveAiosellConfig = async (propertyId) => {
    setAiosellSaving(s => ({ ...s, [propertyId]: true }));
    const config = aiosellConfigs[propertyId] || {};
    const payload = {
      aiosell_enabled:        !!config.aiosell_enabled,
      aiosell_hotel_code:     config.aiosell_hotel_code || "",
      aiosell_room_code:      config.aiosell_room_code || "",
      aiosell_rate_plan_code: config.aiosell_rate_plan_code || "",
    };
    try {
      await api.put(`/aiosell/config/${propertyId}`, payload);
      toast.success("Channel manager config saved");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Save failed: ${msg}`);
    }
    finally { setAiosellSaving(s => ({ ...s, [propertyId]: false })); }
  };

  const generateRelaySecret = (propertyId) => {
    const secret = Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2, "0")).join("");
    setRelayConfigs(prev => ({
      ...prev,
      [propertyId]: { ...(prev[propertyId] || {}), relay_webhook_secret: secret },
    }));
  };

  const saveRelayConfig = async (propertyId) => {
    setRelaySaving(s => ({ ...s, [propertyId]: true }));
    const cfg = relayConfigs[propertyId] || {};
    try {
      await api.put(`/aiosell/relay/${propertyId}`, {
        relay_webhook_url:    cfg.relay_webhook_url    || "",
        relay_webhook_secret: cfg.relay_webhook_secret || "",
      });
      toast.success("Relay config saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setRelaySaving(s => ({ ...s, [propertyId]: false }));
    }
  };

  const syncAiosellBookings = async (propertyId) => {
    setAiosellSyncing(s => ({ ...s, [propertyId]: true }));
    try {
      const { data } = await api.post(`/aiosell/sync/${propertyId}`);
      toast.success(data.message || "Sync complete");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Sync failed: ${msg}`);
    } finally {
      setAiosellSyncing(s => ({ ...s, [propertyId]: false }));
    }
  };

  const fullSyncAiosell = async (propertyId) => {
    setAiosellSyncing(s => ({ ...s, [propertyId]: true }));
    try {
      const { data } = await api.post(`/aiosell/full-sync/${propertyId}`);
      const closed = data.results?.bookings_closed;
      if (data.rates_managed_by_dp && data.inventory_ok) {
        toast.success(`Booked dates re-sent to the channel manager. Smart Pricing manages this unit's rates. Open dates are never changed by a sync.${closed ? ` ${closed} booked date(s) closed.` : ""}`);
      } else if (data.inventory_ok && data.rates_ok) {
        toast.success(`Rates pushed and booked dates re-sent to the channel manager. Open dates are never changed by a sync.${closed ? ` ${closed} booked date(s) closed.` : ""}`);
      } else if (data.no_rate && data.inventory_ok) {
        toast.warning("Booked dates re-sent, but no nightly rate is set. Set a rate so prices push too.", { duration: 7000 });
      } else {
        // Aiosell returned HTTP 200 but rejected the push (e.g. inv_in/rate_in off, or not mapped)
        toast.error(`The channel manager did not accept the sync: ${data.aiosell_message || "contact HostFolio support to check the connection and listing mapping"}`, { duration: 10000 });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Sync failed: ${msg}`);
    } finally {
      setAiosellSyncing(s => ({ ...s, [propertyId]: false }));
    }
  };

  const testAiosellConnection = async (propertyId) => {
    setAiosellTesting(s => ({ ...s, [propertyId]: true }));
    try {
      const { data } = await api.post(`/aiosell/test/${propertyId}`);
      if (data.ok) {
        toast.success(data.message || "Connection verified");
      } else if (data.warning) {
        toast.warning(data.message || "Connected — check your room/rate codes");
      } else {
        toast.error(`Test failed: ${data.message || "Unknown error"}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Connection test failed: ${msg}`);
    }
    finally { setAiosellTesting(s => ({ ...s, [propertyId]: false })); }
  };

  const confirmOfflineBookings = async (propertyId) => {
    try {
      const { data } = await api.post(`/properties/${propertyId}/confirm-offline-bookings`);
      setAiosellConfigs(prev => ({ ...prev, [propertyId]: { ...(prev[propertyId] || {}), calendar_capture: data } }));
      toast.success("Recorded. Offline bookings confirmed for this listing.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record the confirmation");
    }
  };

  const submitAirbnbConnect = async (propertyId) => {
    const inputs = airbnbInputs[propertyId] || {};
    if (!inputs.user_id?.trim() || !inputs.code?.trim()) {
      toast.error("Paste both the Airbnb User ID and Code from the authorization page");
      return;
    }
    setAirbnbSubmitting(s => ({ ...s, [propertyId]: true }));
    try {
      const { data } = await api.post(`/aiosell/airbnb-connect/${propertyId}`, {
        airbnb_user_id: inputs.user_id.trim(),
        airbnb_code:    inputs.code.trim(),
      });
      setAiosellConfigs(prev => ({
        ...prev,
        [propertyId]: {
          ...(prev[propertyId] || {}),
          airbnb_pending:    true,
          airbnb_user_id:    inputs.user_id.trim(),
          airbnb_code:       inputs.code.trim(),
        },
      }));
      if (data.notification && !data.notification.email && !data.notification.whatsapp) {
        toast.warning("Request saved in the admin queue, but the team alert could not be delivered. Contact support@hostfolio.in.", { duration: 10000 });
      } else {
        toast.success("Authorization submitted. HostFolio will complete the listing mapping.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setAirbnbSubmitting(s => ({ ...s, [propertyId]: false }));
    }
  };

  const parseBulkCSV = (text) => {
    const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
    const rows = [];
    lines.forEach((line, i) => {
      if (i === 0 && line.toLowerCase().replace(/\s/g,"").startsWith("property")) return;
      const parts = line.split(",").map(p => p.trim().replace(/^["']|["']$/g, ""));
      if (parts.length < 2) return;
      rows.push({
        name:     parts[0] || "",
        url:      parts[1] || "",
        platform: parts[2] || "airbnb",
        rate:     parseInt(parts[3]) || 0,
      });
    });
    return rows;
  };

  const handleBulkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setBulkParsed(parseBulkCSV(ev.target.result));
    reader.readAsText(file);
  };

  const runBulkImport = async () => {
    if (!bulkParsed.length) return;
    setBulkImporting(true);
    try {
      const form = new FormData();
      const csvContent = "property_name,ical_url,platform,default_rate\n" +
        bulkParsed.map(r => `${r.name},${r.url},${r.platform},${r.rate}`).join("\n");
      form.append("file", new Blob([csvContent], { type: "text/csv" }), "import.csv");
      const { data } = await api.post("/ical/bulk-import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResults(data);
      const reloaded = (await api.get("/ical/feeds")).data;
      setFeeds(Array.isArray(reloaded) ? reloaded : []);
      const reloadedProps = (await api.get("/properties")).data;
      setProps(Array.isArray(reloadedProps) ? reloadedProps : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "property_name,ical_url,platform,default_rate\nSunset Studio Apt,https://www.airbnb.co.in/calendar/ical/12345.ics,airbnb,3500\nOcean View Room,https://www.airbnb.co.in/calendar/ical/67890.ics,airbnb,4500";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "hostfolio_bulk_import_template.csv";
    a.click();
  };

  const addFeed = async () => {
    if (!newFeed.url.trim()) { toast.error("Paste your iCal URL first"); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/ical/add", { ...newFeed, default_rate: Number(newFeed.default_rate) || 0 });
      toast.success(`Feed added — ${data.synced?.total || 0} bookings synced`);
      setNewFeed({ url: "", platform: "airbnb", property_id: "", default_rate: "" });
      const f = (await api.get("/ical/feeds")).data;
      setFeeds(Array.isArray(f) ? f : []);
    } catch (err) { toast.error(err.message || "Failed to add feed"); }
    finally { setAdding(false); }
  };

  const deleteFeed = async (id) => {
    try {
      await api.delete(`/ical/${id}`);
      setFeeds(f => f.filter(x => x.feed_id !== id));
      toast.success("Feed removed");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not remove feed"); }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const res = (await api.post("/ical/sync")).data;
      toast.success(`Synced ${res.synced} feed${res.synced !== 1 ? "s" : ""}`);
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const saveAuto = async (updated) => {
    setAuto(updated);
    try {
      await api.put("/automations", updated);
      toast.success("Saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Settings were not saved. Please retry."); }
  };

  const testEmail = async () => {
    setTestEmail(true);
    try {
      const { data: res } = await api.post("/notifications/test-email");
      if (res.sent) toast.success("Test email sent! Check your inbox.");
      else toast.error(res.detail || "Email send failed");
    } catch { toast.error("Email test failed"); }
    finally { setTestEmail(false); }
  };

  const setAutoField = (key, val) => saveAuto({ ...automations, [key]: val });

  const getExportUrl = async (propertyId) => {
    const makeUrl = (token) => `https://hostfolio.in/ical/export/${token}/${propertyId}.ics`;
    if (exportTokens[propertyId]) {
      await navigator.clipboard.writeText(makeUrl(exportTokens[propertyId]));
      toast.success("Export URL copied! Paste into Airbnb/Booking.com calendar sync settings.");
      return;
    }
    try {
      const { data } = await api.get(`/ical/export-token/${propertyId}`);
      setExportTokens(t => ({ ...t, [propertyId]: data.export_token }));
      await navigator.clipboard.writeText(makeUrl(data.export_token));
      toast.success("Export URL copied! Paste into Airbnb/Booking.com calendar sync settings.");
    } catch { toast.error("Failed to get export URL"); }
  };

  const inputCls = "w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition";

  return (
    <div className="space-y-8 max-w-3xl min-w-0 integrations-page">
      {setup.isPending && <p role="status">Loading your integration settings…</p>}
      {setup.isError && <div role="alert" className="rounded-xl border border-red-300 p-4">
        <p>Could not load your integration settings. Your saved connections have not changed.</p>
        <button type="button" onClick={() => setup.refetch()} className="min-h-[44px] underline">Retry loading</button>
      </div>}
      <OnboardingBanner pageKey="integrations" />
      <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4 space-y-2 text-sm text-slate-700 dark:text-slate-200">
        <p className="font-semibold">Set up booking sites on your phone</p>
        <p>For Airbnb, use the authorization form under each enabled property below. Keep this tab open while you authorize in another tab.</p>
        <p>For Booking.com, MakeMyTrip / Goibibo, Agoda or other sites, HostFolio coordinates your connection. Send the property name, booking site and listing ID to our team. We will guide any account-owner authorization and finish the mapping.</p>
        <a className="inline-flex items-center font-semibold underline" href="mailto:support@hostfolio.in?subject=Booking%20site%20connection%20setup">Contact HostFolio for OTA setup</a>
        <p className="text-xs">Configured means the channel manager details are saved. Verify each booking site's mapping, rates and existing bookings before relying on sync.</p>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-amber-400 tracking-tight">Integrations & Automations</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Connect your calendars and configure automated notifications. This is a one-time setup.</p>
        </div>
        <button onClick={() => { setLocked(l => !l); if (locked) toast("Page unlocked — changes enabled", { icon: "🔓" }); else toast("Page locked", { icon: "🔒" }); }}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold border transition shrink-0 ${locked ? "bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-200" : "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"}`}>
          {locked ? <Lock size={15} weight="fill" /> : <LockOpen size={15} weight="fill" />}
          {locked ? "Locked" : "Unlocked"}
        </button>
      </div>

      {locked && (
        <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 flex items-center gap-2.5 text-sm text-slate-500 dark:text-slate-400">
          <Lock size={14} className="shrink-0" />
          Page is locked to prevent accidental changes. Click <strong className="text-slate-700 dark:text-slate-200 mx-1">Locked</strong> to edit.
        </div>
      )}

      {/* ── Notification Email ── */}
      {automations && (
        <section className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm p-5">
          <div className="flex items-start gap-3 mb-4">
            <Envelope size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-slate-800 dark:text-white text-sm">Notification Email</p>
              <p className="text-xs text-slate-400 mt-0.5">All HostFolio alerts (bookings, check-ins, reminders) go to this address. Guests who reply to automated emails will reach you here too.</p>
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <input type="email" placeholder="e.g. support@hostfolio.in"
              value={automations.notification_email || ""}
              disabled={locked}
              onChange={e => setAuto(a => ({ ...a, notification_email: e.target.value }))}
              onBlur={() => !locked && saveAuto(automations)}
              className={inputCls + " flex-1 " + (locked ? "opacity-60 cursor-not-allowed" : "")} />
            {!locked && (
              <button onClick={() => saveAuto(automations)}
                className="px-4 py-2.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition">
                Save
              </button>
            )}
          </div>
          {!automations.notification_email && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              <Info size={11} /> Not set — notifications will go to your account email instead.
            </p>
          )}
        </section>
      )}

      {/* ── iCal Feeds ── */}
      {properties.length > 0 && aiosellActivePids.size === properties.length ? (
        <section className="bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30 rounded-2xl p-5 flex gap-3">
          <CheckCircle size={18} className="text-green-500 shrink-0 mt-0.5" weight="fill" />
          <div>
            <p className="font-semibold text-green-800 dark:text-green-300 text-sm">Channel manager configured</p>
            <p className="text-xs text-green-700 dark:text-green-400 mt-1">
              Channel manager details are configured for all your properties. Complete booking-site authorization and mapping below before relying on automatic sync.
            </p>
          </div>
        </section>
      ) : (
        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-slate-800 dark:text-white">Calendar Sync</h2>
              <p className="text-xs text-slate-400 mt-0.5">Import bookings from Airbnb, Booking.com, VRBO automatically.</p>
            </div>
            <button onClick={syncAll} disabled={locked || syncing || feeds.length === 0}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition disabled:opacity-40">
              <ArrowsClockwise size={13} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing…" : "Sync All"}
            </button>
          </div>

          {/* How to get iCal URL */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3 mb-4">
            <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700 space-y-1">
              <p><strong>Airbnb:</strong> airbnb.com → Calendar → Availability Settings → Export Calendar → Copy link</p>
              <p><strong>Booking.com:</strong> Extranet → Calendar → iCal → Copy link</p>
              <p><strong>VRBO:</strong> Dashboard → Calendar → Export → Copy iCal link</p>
              <p><strong>MakeMyTrip:</strong> Extranet → My Properties → Calendar → Export Calendar (iCal) → Copy link</p>
              <p><strong>Goibibo:</strong> Partner Portal → Properties → Inventory → Calendar Export → Copy iCal URL</p>
              <p><strong>Agoda:</strong> YCS Partner Portal → Rates & Availability → Calendar Sync → Export iCal</p>
            </div>
          </div>

          {/* Existing feeds — skip feeds belonging to Aiosell-active properties */}
          {feeds.filter(f => !f.property_id || !aiosellActivePids.has(f.property_id)).length > 0 && (
            <div className="space-y-2 mb-4">
              {feeds
                .filter(f => !f.property_id || !aiosellActivePids.has(f.property_id))
                .map((feed) => {
                  const meta = PLATFORM_META[feed.platform] || PLATFORM_META.other;
                  const prop = properties.find(p => p.property_id === feed.property_id || p.id === feed.property_id);
                  return (
                    <motion.div key={feed.feed_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-3 bg-white dark:bg-slate-800 border border-slate-100 rounded-xl px-4 py-3 shadow-sm">
                      <div className={`w-2 h-2 rounded-full ${meta.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.color}`}>{meta.label}</span>
                          {prop && <span className="text-xs text-slate-500 dark:text-slate-400">{prop.name}</span>}
                        </div>
                        <p className="text-xs text-slate-400 truncate mt-0.5">{feed.url}</p>
                      </div>
                      <div className="text-right shrink-0">
                        {feed.last_synced ? (
                          <>
                            <p className="text-[10px] text-green-600 flex items-center gap-1 justify-end">
                              <CheckCircle size={11} weight="fill" />{feed.last_count ?? 0} bookings
                            </p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(feed.last_synced)}</p>
                          </>
                        ) : (
                          <p className="text-[10px] text-slate-400">Not synced yet</p>
                        )}
                      </div>
                      <button onClick={() => !locked && deleteFeed(feed.feed_id)} disabled={locked} className={`text-slate-300 hover:text-red-400 transition ml-1 ${locked ? "opacity-40 cursor-not-allowed" : ""}`}>
                        <Trash size={15} />
                      </button>
                    </motion.div>
                  );
                })}
            </div>
          )}

          {/* Add new feed — property dropdown excludes Aiosell-active properties */}
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-2xl border border-slate-200 dark:border-slate-600 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5"><Link size={13} />Add iCal Feed</p>
              <button onClick={() => { setBulkModal(true); setBulkParsed([]); setBulkResults(null); }}
                className="text-xs text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-1 transition">
                <Plus size={11} /> Bulk Import
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select value={newFeed.platform} disabled={locked} onChange={e => setNewFeed(f => ({ ...f, platform: e.target.value }))}
                className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")}>
                <option value="airbnb">Airbnb</option>
                <option value="booking">Booking.com</option>
                <option value="vrbo">VRBO</option>
                <option value="mmt">MakeMyTrip</option>
                <option value="goibibo">Goibibo</option>
                <option value="agoda">Agoda</option>
                <option value="other">Other</option>
              </select>
              <select value={newFeed.property_id} disabled={locked} onChange={e => setNewFeed(f => ({ ...f, property_id: e.target.value }))}
                className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")}>
                <option value="">All properties</option>
                {nonAiosellProps.map(p => <option key={p.property_id || p.id} value={p.property_id || p.id}>{p.name}</option>)}
              </select>
            </div>
            <input type="url" placeholder="Paste iCal URL (webcal:// or https://)" value={newFeed.url}
              disabled={locked} onChange={e => setNewFeed(f => ({ ...f, url: e.target.value }))}
              className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")} />
            <div className="flex gap-3 items-center">
              <input type="number" placeholder="Default nightly rate ₹ (for revenue tracking)"
                value={newFeed.default_rate} disabled={locked} onChange={e => setNewFeed(f => ({ ...f, default_rate: e.target.value }))}
                className={inputCls + " flex-1" + (locked ? " opacity-60 cursor-not-allowed" : "")} />
              <button onClick={addFeed} disabled={locked || adding}
                className="flex items-center gap-2 px-4 py-2.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50">
                {adding ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  : <><Plus size={14} /> Add Feed</>}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Export Calendar (Reverse Sync) — only for non-Aiosell properties ── */}
      {nonAiosellProps.length > 0 && (
        <section>
          <div className="mb-4">
            <h2 className="font-semibold text-slate-800 dark:text-white">Export Calendar to Airbnb / Booking.com</h2>
            <p className="text-xs text-slate-400 mt-0.5">Copy your HostFolio calendar URL and paste it into Airbnb/Booking.com to automatically block dates.</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex gap-3 mb-4">
            <Info size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-700 space-y-1">
              <p><strong>Airbnb:</strong> Calendar → Availability Settings → Sync Calendars → Import → Paste URL</p>
              <p><strong>Booking.com:</strong> Extranet → Calendar → iCal → Import → Paste URL</p>
              <p><strong>VRBO:</strong> Dashboard → Calendar → Import → Paste URL</p>
            </div>
          </div>
          <div className="space-y-2">
            {nonAiosellProps.map(p => {
              const pid = p.property_id || p.id;
              return (
                <div key={pid} className="flex items-center justify-between bg-white dark:bg-slate-800 border border-slate-100 rounded-xl px-4 py-3 shadow-sm">
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{p.name}</p>
                    {exportTokens[pid]
                      ? <p className="text-xs text-slate-400 font-mono truncate max-w-xs mt-0.5">{`…/ical/export/${exportTokens[pid].slice(0,8)}…/${pid}.ics`}</p>
                      : <p className="text-xs text-slate-400 mt-0.5">Click to generate a permanent export URL</p>
                    }
                  </div>
                  <button onClick={() => getExportUrl(pid)}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition font-medium shrink-0 ml-3">
                    <Copy size={13} /> Copy URL
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Automations ── */}
      {automations && (
        <section>
          <h2 className="font-semibold text-slate-800 dark:text-white mb-1">Automations</h2>
          <p className="text-xs text-slate-400 mb-4">Configure what happens automatically when bookings are created or updated.</p>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-50">
            {[
              { key: "booking_confirmation_email", icon: Envelope, label: "Booking confirmation email", desc: "Send yourself an email when a new booking is created" },
              { key: "checkin_reminder_email", icon: Bell, label: "Check-in reminder email", desc: "Email you 1 day before guest check-in" },
              { key: "checkout_reminder_email", icon: Bell, label: "Check-out reminder email", desc: "Email you 1 day before guest check-out" },
              { key: "auto_create_cleaning_task", icon: CheckCircle, label: "Auto-create cleaning task", desc: "Create a cleaning task automatically after each checkout" },
              { key: "smart_reply_enabled", icon: DeviceMobile, label: "AI Smart Replies in Inbox", desc: "Show AI reply suggestions in the Inbox" },
              { key: "checkin_whatsapp", icon: WhatsappLogo, label: "WhatsApp check-in reminder", desc: "Auto-send check-in instructions to guest via WhatsApp the night before arrival (requires Interakt setup)" },
            ].map(({ key, icon: Icon, label, desc }) => (
              <div key={key} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-start gap-3">
                  <Icon size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
                  </div>
                </div>
                <Toggle value={!!automations[key]} onChange={v => !locked && setAutoField(key, v)} disabled={locked} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* â"€â"€ Email + SMS test â"€â"€ */}
      <section className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-50 dark:divide-slate-700">
        <div className="p-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-slate-800 dark:text-white text-sm">Test Email Notifications</p>
            <p className="text-xs text-slate-400 mt-0.5">Send a test email to verify your notifications are working.</p>
          </div>
          <button onClick={testEmail} disabled={testingEmail}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50">
            {testingEmail ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <><Envelope size={14} /> Send Test</>}
          </button>
        </div>
      </section>

      {/* ── Channel Manager ── */}
      {properties.length > 0 && (
        <section>
          <div className="flex items-start gap-3 mb-4">
            <FlowArrow size={18} className="text-amber-500 mt-0.5 shrink-0" />
            <div>
              <h2 className="font-semibold text-slate-800 dark:text-white">Channel Manager</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Push rates and availability to Airbnb, Booking.com, Agoda, MakeMyTrip, and Expedia automatically.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 rounded-xl p-4 flex gap-3 mb-4">
            <Info size={15} className="text-blue-500 shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
              {isAdmin ? (
                <>
                  <p><strong>Admin:</strong> Enable the toggle and enter the codes for each property. Hosts see only the on/off status.</p>
                  <p>Hotel Code and Room/Rate codes are set up in <span className="font-mono">live.aiosell.com</span> under the HostFolio PMS partner account.</p>
                </>
              ) : (
                <p>When enabled, HostFolio automatically syncs your rates and availability to Airbnb, Booking.com, Agoda, MakeMyTrip, and Expedia. Our team completes the mapping after you authorize access to your booking sites. Make sure your property name in HostFolio matches your Airbnb listing name exactly so we can link it correctly.</p>
              )}
            </div>
          </div>

          {!canUseChannelManager ? (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 border border-amber-200 dark:border-amber-700 rounded-2xl p-6 flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <FlowArrow size={22} weight="duotone" className="text-amber-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-800 dark:text-white text-sm">Channel Manager — Starter plan & above</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm">
                  Automatically sync rates and availability to Airbnb, Booking.com, MakeMyTrip, Agoda, and Expedia. Our team sets up the connection — no technical work needed on your end.
                </p>
              </div>
              <button onClick={() => navigate("/billing")}
                className="px-5 py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition">
                Upgrade to Starter — ₹3,449/mo →
              </button>
            </div>
          ) : (
          <div className="space-y-3">
            {properties.map(prop => {
              const pid = prop.property_id || prop.id;
              const cfg = aiosellConfigs[pid] || {};
              const isEnabled  = !!cfg.aiosell_enabled;
              const isTesting  = !!aiosellTesting[pid];
              const isSaving   = !!aiosellSaving[pid];
              const isSyncing  = !!aiosellSyncing[pid];
              const isHotel    = prop.unit_type === "hotel";
              const propRooms  = prop.rooms || [];
              const hasConfig  = isHotel
                ? !!(cfg.aiosell_hotel_code && propRooms.length > 0 &&
                    propRooms.every(r => roomConfigs[r.room_id]?.aiosell_room_code && roomConfigs[r.room_id]?.aiosell_rate_plan_code))
                : !!(cfg.aiosell_hotel_code && cfg.aiosell_room_code && cfg.aiosell_rate_plan_code);

              return (
                <motion.div key={pid} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm overflow-hidden">
                  {/* Property header row */}
                  <div className="flex flex-wrap gap-3 items-center justify-between px-4 sm:px-5 py-3.5 border-b border-slate-50 dark:border-slate-700">
                    <div className="flex flex-wrap min-w-0 items-center gap-2.5">
                      <div className={`w-2 h-2 rounded-full ${isEnabled && hasConfig ? "bg-green-400" : "bg-slate-300 dark:bg-slate-600"}`} />
                      <p className="text-sm font-semibold text-slate-800 dark:text-white">{prop.name}</p>
                      {isHotel && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 font-semibold">Hotel</span>}
                      {isEnabled && hasConfig && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Configured</span>
                      )}
                      {isEnabled && !hasConfig && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex items-center gap-1">
                          <WarningCircle size={9} weight="fill" /> Codes missing
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 dark:text-slate-500">{isEnabled ? "Enabled" : "Disabled"}</span>
                      <Toggle value={isEnabled} onChange={v => updateAiosellField(pid, "aiosell_enabled", v)} disabled={locked} />
                    </div>
                  </div>

                  {/* Code fields — admin only */}
                  {isAdmin && (
                    <>
                      {/* Hotel Code — always at property level */}
                      <div className={`px-5 py-3 ${isHotel ? "border-b border-slate-50 dark:border-slate-700" : ""} grid grid-cols-1 ${isHotel ? "" : "sm:grid-cols-3"} gap-3`}>
                        <div>
                          <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Hotel Code</label>
                          <input type="text" placeholder="e.g. SANDBOX-PMS"
                            value={cfg.aiosell_hotel_code || ""} disabled={locked}
                            onChange={e => updateAiosellField(pid, "aiosell_hotel_code", e.target.value)}
                            className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")} />
                        </div>
                        {!isHotel && (
                          <>
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Room Type Code</label>
                              <input type="text" placeholder="e.g. ENTIRE-APT"
                                value={cfg.aiosell_room_code || ""} disabled={locked}
                                onChange={e => updateAiosellField(pid, "aiosell_room_code", e.target.value)}
                                className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")} />
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Rate Plan Code</label>
                              <input type="text" placeholder="e.g. BAR"
                                value={cfg.aiosell_rate_plan_code || ""} disabled={locked}
                                onChange={e => updateAiosellField(pid, "aiosell_rate_plan_code", e.target.value)}
                                className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")} />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Per-room codes — hotel only */}
                      {isHotel && propRooms.map((room, ri) => {
                        const rc = roomConfigs[room.room_id] || {};
                        const isSavingRoom = !!roomSaving[room.room_id];
                        return (
                          <div key={room.room_id}
                            className={`ml-6 mr-3 mb-2 ${ri === 0 ? "mt-2" : ""} px-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600`}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{room.name}</p>
                              {!locked && (
                                <button onClick={() => saveRoomConfig(room.room_id)} disabled={isSavingRoom}
                                  className="text-[11px] px-2.5 py-1 bg-amber-400 hover:bg-amber-500 text-white font-semibold rounded-lg transition disabled:opacity-50">
                                  {isSavingRoom ? "Saving…" : "Save room"}
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Room Type Code</label>
                                <input type="text" placeholder="e.g. EXECUTIVE"
                                  value={rc.aiosell_room_code || ""} disabled={locked}
                                  onChange={e => updateRoomField(room.room_id, "aiosell_room_code", e.target.value)}
                                  className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")} />
                              </div>
                              <div>
                                <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Rate Plan Code</label>
                                <input type="text" placeholder="e.g. EP"
                                  value={rc.aiosell_rate_plan_code || ""} disabled={locked}
                                  onChange={e => updateRoomField(room.room_id, "aiosell_rate_plan_code", e.target.value)}
                                  className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Host status message — non-admin only */}
                  {!isAdmin && (
                    <div className="px-5 py-3">
                      {isEnabled && hasConfig ? (
                        <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5">
                          <CheckCircle size={13} weight="fill" /> Channel manager configured. Each booking site still needs authorization and mapping.
                        </p>
                      ) : isEnabled && !hasConfig ? (
                        <div className="space-y-1">
                          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                            <WarningCircle size={13} weight="fill" /> Enabled — HostFolio team will complete your setup shortly
                          </p>
                          <p className="text-xs text-slate-400">It may take up to 24 hours to activate. Make sure your property name here matches your Airbnb listing name exactly.</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-xs text-slate-400">Toggle on to request channel manager setup</p>
                          <p className="text-xs text-slate-400">Tip: use the same property name as your Airbnb listing so we can link it correctly.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action buttons — admin only */}
                  {isAdmin && !locked && (
                    <div className="px-4 sm:px-5 pb-4 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => saveAiosellConfig(pid)}
                        disabled={isSaving}
                        className="flex items-center gap-1.5 px-4 py-2 bg-amber-400 hover:bg-amber-500 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
                      >
                        {isSaving ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Save"}
                      </button>
                      <button
                        onClick={() => testAiosellConnection(pid)}
                        disabled={isTesting || !hasConfig}
                        title={!hasConfig ? "Fill in all three codes first" : "Test API connection"}
                        className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl transition disabled:opacity-40"
                      >
                        {isTesting ? <div className="w-3 h-3 border-2 border-slate-400/40 border-t-slate-600 rounded-full animate-spin" /> : <><ArrowsClockwise size={12} /> Test</>}
                      </button>
                      <button
                        onClick={() => syncAiosellBookings(pid)}
                        disabled={isSyncing || !isEnabled || !hasConfig}
                        title={!isEnabled || !hasConfig ? "Enable and configure the channel manager first" : "Push all existing bookings to the channel manager"}
                        className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl transition disabled:opacity-40"
                      >
                        {isSyncing ? <div className="w-3 h-3 border-2 border-slate-400/40 border-t-slate-600 rounded-full animate-spin" /> : <><ArrowsLeftRight size={12} /> Sync existing</>}
                      </button>
                      <button
                        onClick={() => fullSyncAiosell(pid)}
                        disabled={isSyncing || !isEnabled || !hasConfig}
                        title="Push rates + open availability + close booked dates in one go"
                        className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl transition disabled:opacity-40"
                      >
                        {isSyncing ? <div className="w-3 h-3 border-2 border-slate-400/40 border-t-slate-600 rounded-full animate-spin" /> : <><ArrowsClockwise size={12} /> Sync calendar now</>}
                      </button>
                    </div>
                  )}

                  {/* Non-admin: save + a self-serve "Sync calendar now" */}
                  {!isAdmin && !locked && (
                    <div className="px-4 sm:px-5 pb-4 flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => saveAiosellConfig(pid)}
                        disabled={isSaving}
                        className="flex items-center gap-1.5 px-4 py-2 bg-amber-400 hover:bg-amber-500 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
                      >
                        {isSaving ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Save"}
                      </button>
                      {isEnabled && hasConfig && (
                        <button
                          onClick={() => fullSyncAiosell(pid)}
                          disabled={isSyncing}
                          title="Push your current rates and availability to Airbnb and all connected OTAs now"
                          className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold rounded-xl transition disabled:opacity-50"
                        >
                          {isSyncing ? <div className="w-3 h-3 border-2 border-slate-400/40 border-t-slate-600 rounded-full animate-spin" /> : <><ArrowsClockwise size={12} /> Sync calendar now</>}
                        </button>
                      )}
                    </div>
                  )}

                  {isAdmin && !isHotel && <ChannexConnect propertyId={pid} />}

                  {/* ── Webhook Relay — admin only ── */}
                  {isAdmin && (() => {
                    const relayCfg = relayConfigs[pid] || {};
                    const isSavingRelay = !!relaySaving[pid];
                    const secretVisible = !!relaySecretVisible[pid];
                    const hasRelayUrl = !!(relayCfg.relay_webhook_url || "").trim();
                    return (
                      <div className="border-t border-slate-100 dark:border-slate-700 px-5 py-4">
                        <div className="flex items-center gap-2 mb-3">
                          <ArrowElbowRight size={14} className="text-violet-500 shrink-0" />
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Booking Webhook Relay</p>
                          {hasRelayUrl && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 font-semibold">Configured</span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                          Push booking events (new / modify / cancel) from the channel manager directly to your website or app in real time. Paste your endpoint URL below and share the signing secret with your developer.
                        </p>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Webhook URL</label>
                            <input
                              type="url"
                              placeholder="https://your-site.com/api/hostfolio-webhook"
                              value={relayCfg.relay_webhook_url || ""}
                              disabled={locked}
                              onChange={e => setRelayConfigs(prev => ({
                                ...prev,
                                [pid]: { ...(prev[pid] || {}), relay_webhook_url: e.target.value },
                              }))}
                              className={inputCls + (locked ? " opacity-60 cursor-not-allowed" : "")}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Signing Secret <span className="font-normal normal-case text-slate-400">(X-HostFolio-Signature header)</span></label>
                            <div className="flex gap-2">
                              <div className="relative flex-1">
                                <input
                                  type={secretVisible ? "text" : "password"}
                                  placeholder="Generate or paste a secret"
                                  value={relayCfg.relay_webhook_secret || ""}
                                  disabled={locked}
                                  onChange={e => setRelayConfigs(prev => ({
                                    ...prev,
                                    [pid]: { ...(prev[pid] || {}), relay_webhook_secret: e.target.value },
                                  }))}
                                  className={inputCls + " pr-8" + (locked ? " opacity-60 cursor-not-allowed" : "")}
                                />
                                <button
                                  type="button"
                                  onClick={() => setRelaySecretVisible(v => ({ ...v, [pid]: !secretVisible }))}
                                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                  {secretVisible ? <EyeSlash size={13} /> : <Eye size={13} />}
                                </button>
                              </div>
                              {!locked && (
                                <button
                                  onClick={() => generateRelaySecret(pid)}
                                  className="px-3 py-2 text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl transition shrink-0"
                                >
                                  Generate
                                </button>
                              )}
                              {relayCfg.relay_webhook_secret && (
                                <button
                                  onClick={() => { navigator.clipboard.writeText(relayCfg.relay_webhook_secret); toast.success("Secret copied"); }}
                                  className="px-3 py-2 text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl transition shrink-0"
                                >
                                  <Copy size={13} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Payload reference */}
                        {hasRelayUrl && (
                          <div className="mt-3 bg-slate-900 rounded-xl p-3 overflow-x-auto">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2">Payload the customer will receive</p>
                            <pre className="text-[11px] text-green-400 font-mono leading-relaxed whitespace-pre">{`{
  "action": "book" | "modify" | "cancel",
  "property_id": "${pid}",
  "booking": {
    "booking_id":   "aio_xxx",
    "guest_name":   "Priya Sharma",
    "check_in":     "2026-07-10",
    "check_out":    "2026-07-13",
    "platform":     "airbnb",
    "total_amount": 14200,
    "currency":     "INR",
    "status":       "confirmed"
  }
}`}</pre>
                            <p className="text-[10px] text-slate-500 mt-2">Headers: <span className="font-mono text-slate-400">X-HostFolio-Signature: sha256=&lt;hmac&gt;</span> · <span className="font-mono text-slate-400">X-HostFolio-Event: book</span></p>
                          </div>
                        )}

                        {!locked && (
                          <div className="mt-3 flex items-center gap-2">
                            <button
                              onClick={() => saveRelayConfig(pid)}
                              disabled={isSavingRelay}
                              className="flex items-center gap-1.5 px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
                            >
                              {isSavingRelay ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Save relay"}
                            </button>
                            {hasRelayUrl && (
                              <button
                                onClick={() => {
                                  setRelayConfigs(prev => ({ ...prev, [pid]: { relay_webhook_url: "", relay_webhook_secret: "" } }));
                                }}
                                className="px-4 py-2 text-xs font-semibold text-red-500 hover:text-red-600 transition"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Connect Airbnb (host view) ── */}
                  {!isAdmin && isEnabled && (() => {
                    const ab       = airbnbInputs[pid] || { user_id: "", code: "" };
                    const isPending   = !!cfg.airbnb_pending;
                    const isSubmitting = !!airbnbSubmitting[pid];
                    const alreadyHasCodes = !!(cfg.airbnb_user_id && cfg.airbnb_code);
                    const cap = cfg.calendar_capture || {};
                    const feedOk = !!cap.feed_synced_at;
                    const offlineOk = !!cap.offline_confirmed_at;
                    const captured = feedOk && offlineOk;
                    return (
                      <div className="border-t border-slate-100 dark:border-slate-700 px-4 sm:px-5 py-4">
                        <h3 className="text-sm font-semibold text-slate-800 dark:text-white mb-2">Connect Airbnb</h3>
                        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 mb-3 space-y-2">
                          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Before connecting, HostFolio must hold your full calendar. Once a booking site is connected, dates blocked by hand on that site are no longer visible to any software.</p>
                          <p className={`text-xs ${feedOk ? "text-emerald-700 dark:text-emerald-300" : "text-slate-600 dark:text-slate-300"}`}>
                            {feedOk ? "1. Airbnb calendar export imported." : "1. Add this listing's Airbnb calendar export link under Calendar Sync above and let it sync once. This imports every reservation and blocked date."}
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className={`text-xs ${offlineOk ? "text-emerald-700 dark:text-emerald-300" : "text-slate-600 dark:text-slate-300"}`}>
                              {offlineOk ? "2. Offline bookings confirmed." : "2. Enter every offline booking, walk in and blocked date on the Calendar page, then confirm."}
                            </p>
                            {!offlineOk && (
                              <button onClick={() => confirmOfflineBookings(pid)} className="px-3 py-2 text-xs font-semibold rounded-xl bg-slate-800 text-white hover:bg-slate-700 min-h-[44px]">
                                I have entered every offline booking
                              </button>
                            )}
                          </div>
                        </div>
                        {(isPending || alreadyHasCodes) && (
                          <p role="status" className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 mb-3">
                            {isPending ? "Authorization submitted. Awaiting HostFolio review and listing mapping." : "Authorization was previously submitted. This does not confirm that every listing is mapped."}
                            {" "}You can submit a fresh authorization below if needed.
                          </p>
                        )}
                          <div className="space-y-3">
                            <ol className="text-sm text-slate-600 dark:text-slate-300 list-decimal pl-5 space-y-2">
                              <li>Sign in to the Airbnb account that owns this listing in this phone's Chrome or Safari browser.</li>
                              <li>Open authorization below and review the access requested. It opens a new tab.</li>
                              <li>Return to this tab and paste the User ID and Code from the success page, then submit for this property.</li>
                            </ol>
                            <p className="text-xs text-slate-600 dark:text-slate-300">If you see only a success message, keep that page and contact support@hostfolio.in. No password is needed here.</p>
                            <a
                              href="https://live.aiosell.com/api/v2/cm/airbnb_authorize"
                              target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold rounded-xl transition"
                            >
                              <FlowArrow size={12} /> Open Airbnb Authorization →
                            </a>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div>
                                <label htmlFor={`airbnb-user-${pid}`} className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Airbnb User ID</label>
                                <input
                                  id={`airbnb-user-${pid}`} inputMode="numeric" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={100}
                                  value={ab.user_id}
                                  onChange={e => setAirbnbInputs(prev => ({ ...prev, [pid]: { ...prev[pid], user_id: e.target.value } }))}
                                  placeholder="e.g. 176058855"
                                  className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-rose-400 font-mono"
                                />
                              </div>
                              <div>
                                <label htmlFor={`airbnb-code-${pid}`} className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Code</label>
                                <input
                                  id={`airbnb-code-${pid}`} autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={2048}
                                  value={ab.code}
                                  onChange={e => setAirbnbInputs(prev => ({ ...prev, [pid]: { ...prev[pid], code: e.target.value } }))}
                                  placeholder="e.g. bgpnit5n…"
                                  className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-rose-400 font-mono"
                                />
                              </div>
                            </div>
                            <button
                              onClick={() => submitAirbnbConnect(pid)}
                              disabled={isSubmitting || !captured || !ab.user_id?.trim() || !ab.code?.trim()}
                              className="flex items-center gap-1.5 px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
                            >
                              {isSubmitting ? <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Submit to HostFolio"}
                            </button>
                          </div>
                      </div>
                    );
                  })()}
                </motion.div>
              );
            })}
          </div>
          )}

          {canUseChannelManager && (
          <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5">
            <Info size={11} />
            Authorization and listing mapping are separate steps. After mapping, verify rates, booked dates and incoming reservations for each booking site.
          </p>
          )}
        </section>
      )}

      {/* ── Airbnb Rate Sync (bookmarklet) ── */}
      {properties.length > 0 && rateSync && (
        <section>
          <div className="flex items-start gap-3 mb-4">
            <ArrowsClockwise size={18} className="text-amber-500 mt-0.5 shrink-0" />
            <div>
              <h2 className="font-semibold text-slate-800 dark:text-white">Airbnb Rate Sync (desktop browser)</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Optional desktop bookmark tool. This does not connect your Airbnb account and is not needed for phone setup. Skip it for channel-managed listings.
              </p>
            </div>
          </div>

          {/* Prerequisite: Smart Pricing */}
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 rounded-xl p-4 flex gap-3 mb-4">
            <Info size={15} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
              <p><strong>Turn on Airbnb Smart Pricing first.</strong> Rate sync copies whatever price Airbnb shows for each night. If Smart Pricing is off, every night is the same flat rate.</p>
              <p>Airbnb → Calendar → a date → <span className="font-medium">Pricing</span> → enable <span className="font-medium">Smart Pricing</span>.</p>
            </div>
          </div>

          {/* How it works + install */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm p-5 mb-4">
            <p className="text-sm font-semibold text-slate-800 dark:text-white mb-3">One-time setup</p>
            <ol className="text-xs text-slate-500 dark:text-slate-400 space-y-2 list-decimal pl-4 mb-4">
              <li>Show your browser's <strong>Bookmarks bar</strong> (Ctrl/Cmd + Shift + B).</li>
              <li><strong>Drag the gold button below</strong> onto the bookmarks bar.</li>
              <li>Link each property to its Airbnb listing ID below (one time).</li>
            </ol>
            <p className="text-sm font-semibold text-slate-800 dark:text-white mb-2">Every sync (weekly is plenty)</p>
            <ol className="text-xs text-slate-500 dark:text-slate-400 space-y-2 list-decimal pl-4 mb-4">
              <li>Open your Airbnb <strong>Calendar</strong> page (stay logged in).</li>
              <li>Click the <strong>Sync Airbnb Rates</strong> bookmark. A HostFolio box confirms what was pushed.</li>
            </ol>
            <div className="flex items-center gap-3 flex-wrap">
              {/* href set imperatively so React doesn't sanitise the javascript: URL */}
              <a
                ref={el => { if (el && rateSync.bookmarklet) el.setAttribute("href", rateSync.bookmarklet); }}
                onClick={e => e.preventDefault()}
                draggable="true"
                title="Drag me to your bookmarks bar"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-bold rounded-xl transition cursor-grab active:cursor-grabbing select-none shadow-sm"
              >
                <ArrowsClockwise size={15} weight="bold" /> Sync Airbnb Rates → HostFolio
              </a>
              <span className="text-xs text-slate-400">← drag this to your bookmarks bar</span>
            </div>
          </div>

          {/* Property → Airbnb listing mapping */}
          <div className="space-y-2">
            {(rateSync.properties || []).map(p => {
              const linked   = (listingInputs[p.id] || "").trim();
              const saved    = (p.airbnb_listing_id || "").trim();
              const isSaving  = !!mapSaving[p.id];
              const syncedAt  = p.airbnb_rates_synced_at;
              return (
                <div key={p.id} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl px-4 py-3 shadow-sm">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${saved ? "bg-green-400" : "bg-slate-300 dark:bg-slate-600"}`} />
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{p.name}</p>
                        {!p.aiosell_enabled && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 font-semibold flex items-center gap-1">
                            <WarningCircle size={9} weight="fill" /> Channel manager off
                          </span>
                        )}
                      </div>
                      {syncedAt
                        ? <p className="text-[11px] text-green-600 dark:text-green-400 mt-0.5 ml-4 flex items-center gap-1">
                            <CheckCircle size={11} weight="fill" />
                            Last synced {timeAgo(syncedAt)}{p.airbnb_rates_last_count ? ` · ${p.airbnb_rates_last_count} nights` : ""}
                          </p>
                        : <p className="text-[11px] text-slate-400 mt-0.5 ml-4">Not synced yet</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <input
                        type="text" inputMode="numeric"
                        placeholder="Airbnb listing ID"
                        value={listingInputs[p.id] ?? ""}
                        disabled={locked}
                        onChange={e => setListingInputs(m => ({ ...m, [p.id]: e.target.value }))}
                        className={"w-44 text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 font-mono focus:outline-none focus:border-amber-400" + (locked ? " opacity-60 cursor-not-allowed" : "")}
                      />
                      {!locked && linked !== saved && (
                        <button onClick={() => saveListingMap(p.id)} disabled={isSaving}
                          className="px-3 py-2 text-xs font-semibold bg-amber-400 hover:bg-amber-500 text-white rounded-xl transition disabled:opacity-50">
                          {isSaving ? "…" : "Save"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-3 flex items-start gap-1.5">
            <Info size={11} className="mt-0.5 shrink-0" />
            The listing ID is the number in your Airbnb URL: airbnb.co.in/rooms/<span className="font-mono text-slate-500 mx-0.5">1729660316668682926</span>. Rates only reach other OTAs for properties with the channel manager active.
          </p>
        </section>
      )}

      {/* ── Bulk iCal Import Modal ── */}
      {bulkModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700">
              <div>
                <h2 className="font-semibold text-slate-800 dark:text-white text-sm">Bulk Import iCal Feeds</h2>
                <p className="text-xs text-slate-400 mt-0.5">Upload a CSV to create properties + add feeds in one shot</p>
              </div>
              <button onClick={() => setBulkModal(false)} className="text-slate-400 hover:text-slate-600 transition text-lg leading-none">×</button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {!bulkResults ? (
                <>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-xl p-3 text-xs text-amber-700 dark:text-amber-400 space-y-1">
                    <p className="font-semibold">CSV Format (4 columns):</p>
                    <p className="font-mono">property_name, ical_url, platform, default_rate</p>
                    <p className="font-mono text-amber-500">Sunset Studio, https://airbnb.co.in/calendar/ical/xxx.ics, airbnb, 3500</p>
                    <p className="mt-1">Platform options: <span className="font-mono">airbnb</span> · <span className="font-mono">booking</span> · <span className="font-mono">mmt</span> · <span className="font-mono">goibibo</span> · <span className="font-mono">agoda</span> · <span className="font-mono">vrbo</span></p>
                  </div>

                  <button onClick={downloadTemplate}
                    className="text-xs text-amber-600 hover:text-amber-700 font-semibold flex items-center gap-1.5 transition">
                    <ArrowsClockwise size={12} /> Download CSV Template
                  </button>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Upload CSV file</label>
                    <input type="file" accept=".csv,text/csv" onChange={handleBulkFile}
                      className="block w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-amber-50 file:text-amber-600 hover:file:bg-amber-100 cursor-pointer" />
                  </div>

                  {bulkParsed.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">{bulkParsed.length} rows detected — preview:</p>
                      <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 dark:bg-slate-700 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left text-slate-500 font-semibold">Property</th>
                              <th className="px-3 py-2 text-left text-slate-500 font-semibold">Platform</th>
                              <th className="px-3 py-2 text-left text-slate-500 font-semibold">Rate</th>
                              <th className="px-3 py-2 text-left text-slate-500 font-semibold">URL</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {bulkParsed.map((r, i) => (
                              <tr key={i} className={!r.name || !r.url ? "bg-red-50 dark:bg-red-900/20" : ""}>
                                <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-200 max-w-[140px] truncate">{r.name || <span className="text-red-500">missing</span>}</td>
                                <td className="px-3 py-2 text-slate-500">{r.platform}</td>
                                <td className="px-3 py-2 text-slate-500">{r.rate ? `₹${r.rate}` : "—"}</td>
                                <td className="px-3 py-2 text-slate-400 max-w-[180px] truncate font-mono text-[10px]">{r.url || <span className="text-red-500">missing</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-1">
                    <button onClick={runBulkImport} disabled={bulkImporting || !bulkParsed.length}
                      className="flex items-center gap-2 px-5 py-2.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50">
                      {bulkImporting ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Plus size={14} />}
                      {bulkImporting ? "Importing…" : `Import ${bulkParsed.length} feed${bulkParsed.length !== 1 ? "s" : ""}`}
                    </button>
                    <button onClick={() => setBulkModal(false)} className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700 transition">Cancel</button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: "Properties Created", val: bulkResults.created_properties, color: "text-violet-600" },
                      { label: "Feeds Added", val: bulkResults.feeds_added, color: "text-emerald-600" },
                      { label: "Total Processed", val: bulkResults.total, color: "text-slate-600" },
                    ].map(s => (
                      <div key={s.label} className="bg-slate-50 dark:bg-slate-700 rounded-xl p-3 text-center">
                        <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="max-h-60 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700">
                    <table className="w-full text-xs">
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {bulkResults.results.map((r, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-200">{r.name}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                r.status === "created" ? "bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" :
                                r.status === "added"   ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                                r.status === "skipped" ? "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400" :
                                "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                              }`}>
                                {r.status === "created" ? "✦ new property + feed" :
                                 r.status === "added"   ? "✓ feed added" :
                                 r.status === "skipped" ? "— skipped" : "✕ error"}
                              </span>
                            </td>
                            {r.reason && <td className="px-3 py-2 text-slate-400 text-[10px]">{r.reason}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={() => setBulkModal(false)}
                    className="px-5 py-2.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition">
                    Done
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
